import { INVOICE_TYPE_LABELS } from '../../../../config/invoice/constants';

import {
  ADDITIONAL_SELLER_ID_TYPES,
  ADDITIONAL_BUYER_ID_TYPES,
  PAYMENT_METHOD,
  NOTE_ISSUANCE_REASONS,
} from '../../../../config/invoice/options';
import {
  getLocalizedCurrencyLabel,
  getQrCodeImage,
} from '../../../../helpers/invoiceHelpers';
import moment from 'moment';

export const ROUTES = (type) => {
  return [
    {
      label: 'view_invoice',
      link: '',
    },
    {
      label: 'sme',
      link: 'additional-functionalities/sme',
    },
    {
      label: 'additional-functionalities',
      link: 'additional-functionalities',
    },
  ];
};

export const getTitles = (type) => {
  switch (type) {
    case 'STANDARD_TAX':
      return {
        en: 'Standard E-Invoice',
        ar: 'فاتورة الكترونية',
      };
    case 'STANDARD_CREDIT_NOTE':
    case 'STANDARD_DEBIT_NOTE':
      return {
        en: 'Standard Credit/Debit Note',
        ar: 'إشعار إلكتروني دائن أو مدين',
      };
    case 'SIMPLIFIED_TAX':
      return {
        en: 'Simplified E-Invoice',
        ar: 'فاتورة الكترونية مبسطة',
      };
    case 'SIMPLIFIED_CREDIT_NOTE':
    case 'SIMPLIFIED_DEBIT_NOTE':
      return {
        en: 'Simplified Credit/Debit Note',
        ar: 'إشعار إلكتروني مبسط دائن أو مدين',
      };
  }
};

const getPaymentType = (obj, language) => {
  return (
    (obj.paymentTerms?.paymentMethodCode &&
      PAYMENT_METHOD(language)?.find(
        (method) => method.value === obj.paymentTerms?.paymentMethodCode,
      )?.label) ||
    ''
  );
};

const showBankInfo = (paymentMethodCode) => {
  return !['CASH', 'CREDIT_CARD'].includes(paymentMethodCode);
};

const getPaymentReferenceValue = (obj, language) => {
  const {
    accountNumber,
    accountName,
    bankNameEn,
    paymentTermsEn,
    paymentMethodCode,
  } = obj.paymentTerms || {};

  const paymentType = getPaymentType(obj, language) + ',';
  const terms = paymentTermsEn ? ` Terms: ${paymentTermsEn}` : '';

  if (!showBankInfo(paymentMethodCode)) {
    return paymentType + terms;
  }

  const parts = [
    accountNumber ? ` A/c No: ${accountNumber},` : '',
    accountName ? `A/c Name: ${accountName}, ` : '',
    bankNameEn ? `${bankNameEn},` : '',
    paymentTermsEn ? `Terms: ${paymentTermsEn}` : '',
  ];

  return paymentType + parts.join(' ');
};

const getPurchaseOrderReference = (obj) => {
  const { purchaseOrderNumber, salesOrderNumber } = obj;
  return (
    purchaseOrderNumber +
    (salesOrderNumber ? `, Sales order reference: ${salesOrderNumber}` : '')
  );
};

export const viewObject = ({ obj, language, cities }) => {
  const blocks = [
    {
      key: 'invoiceInfoBlock',
      logo: obj.logoImage,
      sections: [
        {
          key: 'invoiceInfo',
          title: '',
          inputs: [
            {
              label: 'createInvoice.invoice_reference_number',
              value: obj.invoiceReferenceNumber,
            },
            {
              label: 'createInvoice.invoice_issue_date',
              value: obj.invoiceIssueDate
                ? moment(obj.invoiceIssueDate).format('DD MMM, yyyy')
                : '',
            },
            !obj.invoiceType?.startsWith('SIMPLIFIED') && {
              label: 'createInvoice.supply_end_date',
              value: obj.paymentDate
                ? moment(obj.paymentDate).format('DD MMM, yyyy')
                : '',
            },
            {
              label: 'createInvoice.supply_start_date',
              value: obj.supplyDate
                ? moment(obj.supplyDate).format('DD MMM, yyyy')
                : '',
            },
            {
              label: 'createInvoice.currency',
              value: getLocalizedCurrencyLabel(obj.currency, language),
            },
            {
              label: 'createInvoice.exchange_rate',
              value: obj.exchangeRate,
            },
            obj.invoicingPeriodStartDate && {
              label: 'createInvoice.invoicing_period_start_date',
              value: moment(obj.invoicingPeriodStartDate).format(
                'DD MMM, yyyy',
              ),
            },
            obj.invoicingPeriodEndDate && {
              label: 'createInvoice.invoicing_period_end_date',
              value: moment(obj.invoicingPeriodEndDate).format('DD MMM, yyyy'),
            },
          ].filter(Boolean),
        },
      ],
      image: getQrCodeImage(obj.qrCode ?? ''),
    },
    {
      key: 'sellerBlock',
      sections: [
        {
          key: 'identification',
          title: 'createInvoice.seller_identification',
          inputs: [
            {
              label: 'createInvoice.seller_name',
              value: obj.seller.sellerEnglishName,
            },
            {
              label: 'createInvoice.vat_registration_number',
              value: obj.seller.vatNumber,
            },
            {
              label: 'createInvoice.group_vat_registration_number',
              value: obj.seller.groupVatNumber,
            },
            {
              label: 'createInvoice.additional_seller_id_type',
              value:
                (obj?.seller?.additionalSellerIdType &&
                  ADDITIONAL_SELLER_ID_TYPES(language)?.find(
                    (ele) => ele.value === obj.seller.additionalSellerIdType,
                  )?.label) ||
                '',
            },
            {
              label: 'createInvoice.seller_id_number',
              value: obj.seller.additionalSellerId,
            },
            {
              label: 'createInvoice.flat_number',
              value: obj.seller.sellerFlatNumber,
            },
            {
              label: 'createInvoice.building_name',
              value: obj.seller.sellerBuildingName,
            },
            {
              label: 'createInvoice.block',
              value: obj.seller.sellerBlock,
            },
            {
              label: 'createInvoice.street_name',
              value: obj.seller.sellerStreetEn,
            },
            {
              label: 'createInvoice.city',
              value:
                (obj?.seller?.sellerCity &&
                  cities.find((ele) => ele.value === obj.seller.sellerCity)
                    ?.label) ||
                obj?.seller?.sellerCity,
            },
            {
              label: 'createInvoice.country_code',
              value: obj.seller.sellerCountryCode,
            },
          ],
        },
      ],
      image: null,
    },
    obj.thirdPartyInvoiceTransaction && {
      key: 'thirdPartyBlock',
      sections: [
        {
          key: 'identification',
          title: 'createInvoice.third-party-details',
          inputs: [
            {
              label: 'createInvoice.thirdParty_name',
              value: obj.thirdParty.thirdPartyEnglishName,
            },
            {
              label: 'createInvoice.vat_registration_number',
              value: obj.thirdParty.thirdPartyVatNumber,
            },
            {
              label: 'createInvoice.third_party_tin',
              value: obj.thirdParty.thirdPartyTinNumber,
            },
            {
              label: 'createInvoice.flat_number',
              value: obj.thirdParty.thirdPartyFlatNumber,
            },
            {
              label: 'createInvoice.building_name',
              value: obj.thirdParty.thirdPartyBuildingName,
            },
            {
              label: 'createInvoice.block',
              value: obj.thirdParty.thirdPartyBlock,
            },
            {
              label: 'createInvoice.street_name',
              value: obj.thirdParty.thirdPartyStreetName,
            },
            {
              label: 'createInvoice.city',
              value:
                (obj?.thirdParty?.thirdPartyCity &&
                  cities.find((ele) => ele.value === obj.buyer.thirdPartyCity)
                    ?.label) ||
                obj?.thirdParty?.thirdPartyCity,
            },
            {
              label: 'createInvoice.country_code',
              value: obj.thirdParty.thirdPartyCountry,
            },
          ],
        },
      ],
      image: null,
    },
    {
      key: 'buyerBlock',
      sections: [
        {
          key: 'identification',
          title: 'createInvoice.buyer_identification',
          inputs: [
            {
              label: 'createInvoice.buyer_name',
              value: obj.buyer.buyerEnglishName,
            },
            {
              label: 'createInvoice.vat_registration_number',
              value: obj.buyer.buyerVatNumber,
            },
            {
              label: 'createInvoice.additional_buyer_id_type',
              value:
                (obj.buyer.additionalBuyerIdType &&
                  ADDITIONAL_BUYER_ID_TYPES(language)?.find(
                    (ele) => ele.value === obj.buyer.additionalBuyerIdType,
                  )?.label) ||
                '',
            },
            {
              label: 'createInvoice.additional_buyer_id_number',
              value: obj.buyer.additionalBuyerId,
            },
            {
              label: 'createInvoice.flat_number',
              value: obj.buyer.buyerFlatNumber,
            },
            {
              label: 'createInvoice.building_name',
              value: obj.buyer.buyerBuildingName,
            },
            {
              label: 'createInvoice.block',
              value: obj.buyer.buyerBlock,
            },
            {
              label: 'createInvoice.street_name',
              value: obj.buyer.buyerStreetEn,
            },
            {
              label: 'createInvoice.city',
              value:
                (obj?.buyer?.buyerCity &&
                  cities.find((ele) => ele.value === obj.buyer.buyerCity)
                    ?.label) ||
                obj?.buyer?.buyerCity,
            },
            {
              label: 'createInvoice.country_code',
              value: obj.buyer.buyerCountryCode,
            },
          ],
        },
      ],
      image: null,
    },
    {
      key: 'lineItemsBlock',
      title: 'createInvoice.line_items',
      items: [...obj.invoiceItems],
      documentDiscount: obj.invoiceLevelDiscount,
      prepayment: obj.prepayment,
      roundingAmount: obj.roundingAmount,
    },
    {
      key: 'notesBlock',
      sections: [
        {
          key: 'notes',
          title: 'createInvoice.notes',
          inputs: [
            {
              label: 'createInvoice.special_tax_treatment',
              value: obj.specialTaxTreatmentEn,
            },
            {
              value: obj.enNotes,
              valueClassName: 'notes-value',
            },
          ],
        },
      ],
      image: null,
    },
    {
      key: 'paymentTermsBlock',
      sections: [
        {
          key: 'terms',
          title: 'createInvoice.payment_reference',
          inputs: [
            {
              value: getPaymentReferenceValue(obj, language),
              valueClassName: 'payment-reference-value',
            },
          ],
        },
      ],
      image: null,
    },
    (obj.purchaseOrderNumber || obj.salesOrderNumber) && {
      key: 'purchaseOrderReferenceBlock',
      sections: [
        {
          key: 'orderReference',
          title: 'createInvoice.purchase_order_reference',
          inputs: [
            {
              value: getPurchaseOrderReference(obj),
              valueClassName: 'purchase-order-value',
            },
          ],
        },
      ],
    },
    obj.invoiceType.indexOf('NOTE') !== -1 && {
      key: 'referenceBlock',
      sections: [
        {
          key: 'reference',
          title: 'createInvoice.reference',
          inputs: [
            {
              label: 'createInvoice.reference_to_original_invoice',
              value: obj.originalInvoiceReference,
            },
            {
              label: 'createInvoice.original_invoice_date',
              value: moment(obj.precedingInvoiceIssueDate).format(
                'DD MMM, yyyy',
              ),
            },
            {
              label: 'createInvoice.gross_amount_of_referenced_invoice',
              value: '',
            },
            {
              label: 'createInvoice.reason_for_credit_debit_note_issuance',
              value:
                (obj.reasonOfNote &&
                  NOTE_ISSUANCE_REASONS(language)?.find(
                    (reason) => reason.value === obj.reasonOfNote,
                  )?.label) ||
                '',
            },
          ],
        },
      ],
      image: null,
    },
  ];

  return blocks;
};
